import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportLcService } from '../../services/exportLc.service';
import { PERMISSIONS } from '../../constants/permissions';
import { CreateLcAmendmentRequest, LcAmendment } from '../../interfaces/commercial.interface';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();
const EMPTY = { amendmentDate: today(), description: '', newLcAmount: '', newExpiryDate: '', newLatestShipmentDate: '' };

/**
 * R-6: append-only. This panel deliberately offers no edit or delete control for an existing
 * amendment row — only "Add amendment" (create), matching the backend controller which has no
 * PUT/DELETE route for amendments.
 */
const LcAmendmentPanel: React.FC<{ lcId: string }> = ({ lcId }) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);

    const [rows, setRows] = useState<LcAmendment[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await ExportLcService.listAmendments(institutionId, lcId, 0, 100);
            setRows(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load amendments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [lcId]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const handleCreate = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const dto: CreateLcAmendmentRequest = {
            amendmentDate: form.amendmentDate,
            description: form.description.trim() || undefined,
            newLcAmount: form.newLcAmount.trim() ? Number(form.newLcAmount) : undefined,
            newExpiryDate: form.newExpiryDate || undefined,
            newLatestShipmentDate: form.newLatestShipmentDate || undefined,
        };
        try {
            setSaving(true);
            await ExportLcService.createAmendment(institutionId, lcId, dto);
            toast.showSuccess('Amendment recorded');
            setOpen(false);
            setForm({ ...EMPTY });
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to record amendment');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Amendments</Typography>
                {canManage && (
                    <Button data-help="commercial.lcAmend.add" startIcon={<AddIcon />} variant="outlined" onClick={() => setOpen(true)}>
                        Add Amendment
                    </Button>
                )}
            </Box>
            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.lcAmend.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.lcAmend.col.number">#</TableCell>
                                <TableCell data-help="commercial.lcAmend.col.date">Date</TableCell>
                                <TableCell data-help="commercial.lcAmend.col.description">Description</TableCell>
                                <TableCell align="right" data-help="commercial.lcAmend.col.amount">New Amount</TableCell>
                                <TableCell data-help="commercial.lcAmend.col.expiry">New Expiry</TableCell>
                                <TableCell data-help="commercial.lcAmend.col.shipment">New Latest Shipment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(a => (
                                <TableRow key={a.id}>
                                    <TableCell>{a.amendmentNumber}</TableCell>
                                    <TableCell>{formatDate(a.amendmentDate)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{a.description ?? '—'}</TableCell>
                                    <TableCell align="right">{a.newLcAmount ?? '—'}</TableCell>
                                    <TableCell>{a.newExpiryDate ? formatDate(a.newExpiryDate) : '—'}</TableCell>
                                    <TableCell>{a.newLatestShipmentDate ? formatDate(a.newLatestShipmentDate) : '—'}</TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center">No amendments recorded.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.lcAmend.dialog.page' } as any}>
                <DialogTitle>Add Amendment</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcAmend.dateField" label="Amendment Date" type="date" fullWidth required
                                InputLabelProps={{ shrink: true }} value={form.amendmentDate} onChange={set('amendmentDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcAmend.amountField" label="New LC Amount (optional)" type="number" fullWidth
                                value={form.newLcAmount} onChange={set('newLcAmount')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcAmend.expiryField" label="New Expiry Date (optional)" type="date" fullWidth
                                InputLabelProps={{ shrink: true }} value={form.newExpiryDate} onChange={set('newExpiryDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcAmend.shipmentField" label="New Latest Shipment (optional)" type="date" fullWidth
                                InputLabelProps={{ shrink: true }} value={form.newLatestShipmentDate} onChange={set('newLatestShipmentDate')} />
                        </Grid>
                        <Grid size={12}>
                            <TextField data-help="commercial.lcAmend.descField" label="Description" fullWidth multiline rows={2} value={form.description} onChange={set('description')} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.lcAmend.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.lcAmend.save" variant="contained" disabled={saving} onClick={handleCreate}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LcAmendmentPanel;
